import { Formik, FormikProps, useFormikContext } from "formik";
import {
  HouseContentsCalculatorFormValues,
  StandardOptions,
} from "./interfaces";

import Box from "@storeblocks/box";
import Modal from "@storeblocks/modal";
import { Option } from "@storeblocks/select";
import { maxContentsSum } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/houseProductsUtils";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { Input } from "Components/Form/Input";
import { InputMode } from "Components/Form/Input/inputTypes";
import { Select } from "Components/Form/Select";
import { VALUE_TYPE } from "Components/NumberValue";
import { useTexts } from "Components/TextContext";
import { Button } from "Components/TextContext/commonComponentEnums";
import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { getPriceFormatFromValue } from "Formatting/index";
import { buttonTrackId } from "Utils/analytics";
import { isDefined } from "Utils/functions";
import { getError } from "Utils/productUtils";
import React from "react";
import { Feedback } from "./Feedback";
import { useCalculateInsuranceAmountQuery } from "./hooks";
import { validationSchema } from "./validation";

interface Props {
  product: ProductBackendName;
  isVisible: boolean;
  toggleVisible: (arg: boolean) => void;
  handleSubmit: (calculatedRecommendation: number) => void;
  isFullRental: boolean;
  errorMessage: string;
  trigger: React.ReactNode;
}

export const HouseContentsCalculator = ({
  product,
  isVisible,
  toggleVisible,
  handleSubmit,
  isFullRental,
  errorMessage,
  trigger,
}: Props): React.ReactElement => {
  const handleCalculation = (
    formPropsValues: Required<HouseContentsCalculatorFormValues>,
  ): void => {
    toggleVisible(!isVisible);
    handleSubmit(formPropsValues.suggestedInsuranceAmount);
  };

  const { getText } = useTexts();

  const renderFooter = (
    formProps: FormikProps<HouseContentsCalculatorFormValues>,
  ): React.ReactNode => {
    const sum = isDefined(formProps.values.suggestedInsuranceAmount)
      ? getPriceFormatFromValue(
          formProps.values.suggestedInsuranceAmount,
          VALUE_TYPE.NOK,
        )
      : "";

    return (
      <>
        <ThemedButton
          id="addCoverageSum"
          trackId={buttonTrackId("confirm-house-contents-calculator-sum")}
          onClick={formProps.submitForm}
          marginRight="32px"
          aria-label={getText(
            "housecontentscalculator.arialabel.addcoveragesumbutton",
            { variables: { houseContentsCalculatorCoverageSum: sum } },
          )}
        >
          {getText("housecontentscalculator.button.addcoveragesum")}
        </ThemedButton>

        <ThemedButton
          id="closeModalButton"
          trackId={buttonTrackId("closeModalButton")}
          onClick={() => toggleVisible(!isVisible)}
        >
          {getText(Button.CANCEL)}
        </ThemedButton>
      </>
    );
  };

  return (
    <Formik
      initialValues={{ standard: StandardOptions.STANDARD_NOT_SELECTED }}
      enableReinitialize
      validationSchema={validationSchema()}
      validateOnMount
      onSubmit={(formValues: Required<HouseContentsCalculatorFormValues>) => {
        handleCalculation(formValues);
      }}
    >
      {(formProps: FormikProps<HouseContentsCalculatorFormValues>) => {
        return (
          <Modal
            open={isVisible}
            trigger={trigger}
            content={
              <HouseContentsCalculatorFormContent
                product={product}
                isFullRental={isFullRental}
                errorMessage={errorMessage}
              />
            }
            heading={getText("housecontentscalculator.headings.mainheading")}
            footer={renderFooter(formProps)}
            onClose={() => toggleVisible(!isVisible)}
          />
        );
      }}
    </Formik>
  );
};

const HouseContentsCalculatorFormContent = ({
  product,
  isFullRental,
  errorMessage,
}: Pick<
  Props,
  "product" | "isFullRental" | "errorMessage"
>): React.ReactElement => {
  const formProps = useFormikContext<HouseContentsCalculatorFormValues>();
  const {
    data: suggestedInsuranceAmount,
    isLoading: isLoadingSuggestedInsuranceAmount,
  } = useCalculateInsuranceAmountQuery();
  const { getText } = useTexts();
  const maxSumAllowed = maxContentsSum(product, isFullRental);

  const standardOptions = useHouseContentsStandardOptions();

  return (
    <Box
      p={24}
      flexDirection="column"
      aria-live="polite"
      aria-busy={formProps.isValidating}
    >
      <Input
        large
        id="people"
        name="people"
        label={getText("housecontentscalculator.label.people")}
        type="number"
        error={getError("people", formProps)}
        inputProps={{ autoComplete: "off" }}
        inputMode={InputMode.NUMERIC}
      />
      <Select
        large
        id="standard"
        name="standard"
        label={getText("housecontentscalculator.label.standard")}
        hint={getText("housecontentscalculator.hint.standard")}
        error={getError("standard", formProps)}
        options={standardOptions}
      />
      <Input
        large
        id="rooms"
        name="rooms"
        inputProps={{ autoComplete: "off" }}
        label={getText("housecontentscalculator.label.rooms")}
        hint={getText("housecontentscalculator.hint.rooms")}
        type="number"
        error={getError("rooms", formProps)}
        inputMode={InputMode.NUMERIC}
      />

      {(suggestedInsuranceAmount ||
        (formProps.isValid && isLoadingSuggestedInsuranceAmount)) && (
        <Feedback
          suggestedInsuranceAmount={suggestedInsuranceAmount}
          isLoadingSuggestedInsuranceAmount={isLoadingSuggestedInsuranceAmount}
          errorMessage={errorMessage}
          isCoverageTooHigh={
            !!suggestedInsuranceAmount &&
            suggestedInsuranceAmount > maxSumAllowed
          }
        />
      )}
    </Box>
  );
};

const useHouseContentsStandardOptions = (): Option[] => {
  const { getText } = useTexts();

  const getTranslationForStandardOption = (option: StandardOptions): string => {
    switch (option) {
      case StandardOptions.STANDARD_LOW:
        return getText(
          "housecontentscalculator.optionlabel.6-standard-standard_low",
        );
      case StandardOptions.STANDARD_MEDIUM_LOW:
        return getText(
          "housecontentscalculator.optionlabel.5-standard-standard_below_medium",
        );
      case StandardOptions.STANDARD_MEDIUM:
        return getText(
          "housecontentscalculator.optionlabel.4-standard-standard_medium",
        );
      case StandardOptions.STANDARD_MEDIUM_HIGH:
        return getText(
          "housecontentscalculator.optionlabel.3-standard-standard_medium_high",
        );
      case StandardOptions.STANDARD_HIGH:
        return getText(
          "housecontentscalculator.optionlabel.2-standard-standard_better",
        );
      case StandardOptions.STANDARD_VERY_HIGH:
        return getText(
          "housecontentscalculator.optionlabel.1-standard-standard_very_high",
        );
      case StandardOptions.STANDARD_NOT_SELECTED:
      default:
        return getText("housecontentscalculator.placeholder.standard");
    }
  };

  return Object.values(StandardOptions).map(
    (option): Option => ({
      value: option,
      label: getTranslationForStandardOption(option),
    }),
  );
};
